import { SettingsService } from './../../../data/repositories/settings/settings.service';
import { UserService } from './../../../data/repositories/user/user.service';
import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BehaviorSubject, forkJoin, Observable, Subject } from 'rxjs';
import { cloneDeep, cloneDeepWith, orderBy } from 'lodash-es';
import { Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { EnvironmentManagerService } from '../../../shared/services/environment-manager/environment-manager.service';
import { DatacontextService } from 'src/app/data/datacontext.service';

const CE_DATA = "CE_DATA";

@Injectable({
  providedIn: 'root'
})
export class AppSelectorService {
  private subject = new Subject<any>();
  private appSubject = new BehaviorSubject<any>(undefined);
  private appListServiceReady = new Subject<any>();
  private appCountDetails = new Subject<any>();
  public cePanelDetailsReady = new Subject<any>();
  private selectedApp: any = {};
  private ogList: any = [];
  private appEnvMap = {};
  private appMap: any = {
    production: [],
    stage: []
  };
  private appDetailsCount = {};
  private appListByUsage = [];
  private companyDetails = {};
  private subscription: Subscription;
  private hasAppDetailsLoadedSuccessfully: Boolean = false;
  private localStorageKeyForRecentApps: any = '';
  private localStorageKeyForLastSelectedApp: any = '';
  private apiParams: any = {
    switch_type: "PX_TO_CEE"
  };
  private popupRef: any = '';
  private redirectPageForCE: any = '';
  redirectArgumentForCE: any;
  public toggleSidebar$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private router: Router,
    private environmentManagerService: EnvironmentManagerService,
    private datacontextService: DatacontextService,
    private settingsService: SettingsService,
    private userService: UserService) {
    if (this.authenticationService.isAuthenticated() && !this.authenticationService.isIframe()) {
      this.loadApps();
      this.loadAppsDetailsCount();
      this.promisifyAppListAndCount().then(() => {
        this.hasAppDetailsLoadedSuccessfully = true;
        this.getAppListFromLocalStorage();
        this.updateLocalStorageForAppList(this.selectedApp);
      });
    }
    // wait for onsite auth to be successful before loading apps
    this.authenticationService.onsiteUserAuth$.subscribe(() => {
      this.loadApps();
    });
    this.subscription = this.environmentManagerService.getEnvironmentChangedListener().subscribe(env => {
      this._switchApp(env);
    });
  }

  setCEPanelDetailsReady(flag: boolean) {
    this.cePanelDetailsReady.next(flag);
  }

  getAppDetailsCount() {
    return this.appDetailsCount;
  }

  getAppListByUsage() {
    return this.appListByUsage;
  }

  getAppListFromLocalStorage() {
    const companyDetails: any = this.companyDetails;
    const companyName = companyDetails.name.split(' ').join('');
    this.localStorageKeyForRecentApps = `${companyName}_appListByUsage`;
    this.appListByUsage = JSON.parse(localStorage.getItem(this.localStorageKeyForRecentApps));
    if (!this.appListByUsage) {
      this.setAppListToLocalStorage();
    }
  }

  setAppListToLocalStorage(appList = []) {
    localStorage.setItem(this.localStorageKeyForRecentApps, JSON.stringify(appList));
    this.appListByUsage = appList;
  }

  updateLocalStorageForAppList(appObj: any = {}) {
    const index = this.appListByUsage.indexOf(appObj.id);
    let apps = [];
    if (typeof appObj === 'object' && appObj.hasOwnProperty('id') && this.setAppListToLocalStorage) {
      if (index >= 0) {
        this.appListByUsage.splice(index, 1);
      }
      apps = [appObj.id, ...this.appListByUsage];
      this.setAppListToLocalStorage(apps);
    }
  }

  public loadAppsDetailsCount() {
    this.appDetailsCount = {};
    return this.datacontextService.apps.getAppsDetailsCount((data: any) => {
      this.appDetailsCount = data.api_response.app_wise_count;
      this.appCountDetails.next(true);
    });
  }

  public loadApps(appId?: string, cb?: Function) {
    return this.datacontextService.apps.getAllApps().subscribe((data: any) => {
      if (!data || data.is_error) {
        this.appListServiceReady.next(false);
        return false;
      }
      // set app details loaded to true for onsite journeys
      if (this.authenticationService.isIframe()) {
        this.hasAppDetailsLoadedSuccessfully = true;
      }

      if (!data.api_response.apps.length) {
        this.router.navigate(['overview']);
      }

      this.setCompanyDetails(data.api_response.company_details);
      this.setAppList(data.api_response.apps);
      this.appSubject.next(appId);
      this.appListServiceReady.next(true);
      if (cb) {
        return cb();
      }
    });
  }

  mapAppIdWithApp(apps) {
    return apps.reduce((incr, cur) => {
      if (!incr.hasOwnProperty(cur.id)) {
        incr[cur.id] = cur;
      }
      return incr;
    }, {});
  }

  unMapAppIdWithApp(appsListById) {
    const appKeys: any = Object.keys(appsListById);
    return appKeys.reduce((incr: any, curId: any) => {
      incr.push(appsListById[curId]);
      return incr;
    }, []);
  }

  public appsLoaded(): boolean {
    return this.ogList && this.ogList.length > 0;
  }

  public getAppsLoaded(): Observable<any> {
    return this.appSubject.asObservable();
  }

  public getAppListServiceReady(): Observable<any> {
    return this.appListServiceReady.asObservable();
  }

  public getAppCountDetailsReady(): Observable<any> {
    return this.appCountDetails.asObservable();
  }

  public reloadAndSelectApp(appId: string, cb: Function) {
    this.loadApps(appId, cb);
  }

  public reload() {
    this.loadApps();
  }

  private setAppList(appList: any) {
    this.ogList = appList;
    this._processAppList();

    const companyDetails: any = this.companyDetails;
    const companyName = companyDetails.name.split(' ').join('');
    this.localStorageKeyForLastSelectedApp = `${companyName}_selectedApp`;
    // Check if Local storage has an app and set the same app
    const selectedAppFromLocalStorage = JSON.parse(localStorage.getItem(this.localStorageKeyForLastSelectedApp));
    if (selectedAppFromLocalStorage && selectedAppFromLocalStorage[this.environmentManagerService.getCurrentEnvironment()]) {
      const idx = this._getAppIndex(
        selectedAppFromLocalStorage[this.environmentManagerService.getCurrentEnvironment()].id,
        this.appMap[this.environmentManagerService.getCurrentEnvironment()]
      );
      if (idx < 0) {
        this.setSelectedApp(this.appMap[this.environmentManagerService.getCurrentEnvironment()][0]);
      } else {
        this.setSelectedApp(this.appMap[this.environmentManagerService.getCurrentEnvironment()][idx]);
      }
    } else {
      // Set the first app as selected
      this.setSelectedApp(this.appMap[this.environmentManagerService.getCurrentEnvironment()][0]);
    }

  }

  _getAppIndex(appId: string, list: any[]) {
    return list.findIndex(x => x.id === appId);
  }

  /**
   * waits for the appList data to be availble
   */
  promisifyForAppData() {
    return new Promise<void>((resolve, reject) => {
      if (this.hasAppDetailsLoadedSuccessfully) {
        resolve();
        return;
      }
      this.getAppListServiceReady().subscribe(data => data ? resolve(data) : reject({}));
    });
  }

  promisifyForAppDetailsCount() {
    return new Promise<void>((resolve, reject) => {
      if (this.hasAppDetailsLoadedSuccessfully) {
        resolve();
        return;
      }
      this.getAppCountDetailsReady().subscribe(data => data ? resolve(data) : reject({}));
    });
  }

  promisifyAppListAndCount() {
    return Promise.all([
      this.promisifyForAppData(),
      this.promisifyForAppDetailsCount(),
    ]);
  }

  hasAppDetailsLoaded(): Boolean {
    return this.hasAppDetailsLoadedSuccessfully;
  }

  public getApplist(environment: string) {
    if (environment in this.appMap) {
      return this.appMap[environment];
    }
    return [];
  }

  public getAppPlatformTitle(os) {
    switch (os) {
      case 'be': return 'Server-Side';
      case 'android': return 'Android';
      case 'ios': return 'iOS';
      case 'web': return 'Web';
    }
  }

  public getAppNameForId(appId: string, env: string) {
    let appList = this.getApplist(env);

    let filterListById = appList.filter(app => app.id === appId);
    if (filterListById.length) {
      return filterListById[0].name;
    }
    return '';
  }

  private setCompanyDetails(dets: Object) {
    this.companyDetails = dets;
  }

  public getCompanyDetails() {
    return this.companyDetails;
  }

  public getAppChangedListener(): Observable<any> {
    return this.subject.asObservable();
  }

  public setSelectedApp(appObj: any) {
    if (!appObj || !appObj.name || !appObj.id) {
      return false;
    }
    // Check if the selected app has already been selected
    if ('id' in this.selectedApp && appObj.id === this.selectedApp.id) {
      return true;
    }
    this.updateLocalStorageForAppList(appObj);
    this.selectedApp = appObj;
    this.subject.next(this.selectedApp);
    this.appSubject.next(this.selectedApp.id);
    // Push the selected app to Local storage
    const selectedAppData = {};
    selectedAppData[this.environmentManagerService.getCurrentEnvironment()] = this.selectedApp;
    localStorage.setItem(this.localStorageKeyForLastSelectedApp, JSON.stringify(selectedAppData));
    return true;
  }

  public getSelectedApp() {
    return cloneDeep(this.selectedApp);
  }

  public getAppEnvironments(appId: string) {
    if (appId in this.appEnvMap) {
      return this.appEnvMap[appId];
    }
    return {};
  }

  public getAppObj(appId: string) {
    return this._findApp(appId, this.ogList);
  }

  private _findApp(appId: string, list: any) {
    return list.find((app: any) => {
      return app.id === appId;
    });
  }

  /**
   * filter apps by environment and add to appMap
   */
  private _processAppList() {
    const rawProductionApps: any[] = [];
    const rawStageApps: any[] = [];
    this.ogList.forEach((app: any) => {
      if (app.env === 'production') {
        const temp = cloneDeepWith(app, (val => {
          val.value = val.name;
          return val;
        }));
        rawProductionApps.push(temp);
      } else {
        const temp = cloneDeepWith(app, (val => {
          val.value = val.name;
          return val;
        }));
        rawStageApps.push(temp);
      }
    });

    this.appMap.production = orderBy(rawProductionApps, 'name', 'asc');
    this.appMap.stage = orderBy(rawStageApps, 'name', 'asc');
    this.appEnvMap = this._createAppEnvMap(this.appMap.production);
  }

  private _createAppEnvMap(appList: any) {
    const tempMap = {};
    appList.forEach((app: any) => {
      tempMap[app.id] = {};
      tempMap[app.id].production = app.id;
      tempMap[app.id].stage = app.env_map && app.env_map.stage ? app.env_map.stage : {};
    });
    return tempMap;
  }

  private _switchApp(env: string) {
    const sisterApp = this._getNewAppObj(this.selectedApp.id);
    this.setSelectedApp(sisterApp);
  }

  public _getNewAppObj(appId: string) {
    const newEnv = this.environmentManagerService.getCurrentEnvironment();
    return this._findApp(this.appEnvMap[this.selectedApp.id][newEnv], this.appMap[newEnv]);
  }

  public postFormDataAndRedirect(redirect_url: string, postData?: any) {
    var f = document.createElement('form');
    f.action = redirect_url;
    f.method = 'POST';

    if (postData) {
      for (var key in postData) {
        if (postData.hasOwnProperty(key)) {
          let i = document.createElement('input');
          i.type = 'hidden';
          i.name = key;
          i.value = postData[key];
          f.appendChild(i);
        }
      }
    }

    document.body.appendChild(f);
    f.submit();
  }

  handleSwitchPanel = (redirectPage, segmentId?) => {
    this.popupRef = window.open("/redirecting-to-netcore-ce", "_blank");
    this.redirectPageForCE = redirectPage;
    this.redirectArgumentForCE = segmentId;

    forkJoin([
      this.userService.getuserCompanyDetails(),
      this.userService.fetchUserInfoViaNetwork()
    ]).subscribe((data: any) => {
      try {
        if (data[0] && data[0].api_response) {
          const userCompanyDetails = data[0].api_response;
          this.apiParams.px_client_id = userCompanyDetails.companies[0].id;
        }

        if (data[1] && data[1].api_response) {
          const currentUserInfo = data[1].api_response;
          this.apiParams.email = currentUserInfo.email;
        }
        this.fetchAppId();
      } catch (error) { }
    });
  }

  fetchAppId() {
    let selectedApp = this.getSelectedApp();
    let appId = selectedApp.id;

    if (!selectedApp) {
      this.loadApps(null, () => {
        selectedApp = this.getSelectedApp();
        appId = selectedApp.id;
        this.getAppDetail(appId);
      });
      return;
    }

    this.getAppDetail(appId);
  }

  getAppDetail = (appId) => {
    this.settingsService.getAppDetails(appId).subscribe((resp: any) => {
      let appDetail = resp.api_response;
      this.apiParams.smt_client_region = appDetail.smt_client_region;
      this.apiParams.smt_client_id = appDetail.smt_client_id;
      this.apiParams.smt_app_id = appDetail.smt_app_id;
      this.fetchCEPanelDetail(this.apiParams);
    })
  }

  fetchCEPanelDetail(params?) {
    let data = Object.keys(params).map(key => key + '=' + params[key]).join('&');

    this.authenticationService.fetchCEPanelDetail(data).subscribe((resp: any) => {
      resp.redirectPage = this.redirectPageForCE;
      resp.segmentId = this.redirectArgumentForCE;
      setTimeout(() => {
        this.popupRef.postMessage(resp, "*");
      }, 1000);
    });
  }

  removeCEDataFromLocalStorage() {
    localStorage.removeItem(CE_DATA);
  }

  getCEDataFromLocalStorage() {
    let storedData: any = localStorage.getItem(CE_DATA);
    if (storedData) {
      storedData = JSON.parse(storedData);
    }
    return storedData;
  }

  setCEDataFromLocalStorage(value) {
    localStorage.setItem(CE_DATA, JSON.stringify(value));
  }
}

/**
 * I'm writing code instead of decorating the room
    _\/_
     /\
     /\
    /  \
    /~~\o
   /o   \
  /~~*~~~\
 o/    o \
 /~~~~~~~~\~`
/__*_______\
     ||
   \====/
    \__/
 */
